import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Cloud,
  Play,
  Globe,
  Database,
  MapPin
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const StatusBadge = ({ status, message }) => {
  if (status === 'ok' || status === 'healthy' || status === 'success') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
        <CheckCircle className="w-3 h-3" />
        {message || 'OK'}
      </span>
    );
  }
  if (status === 'warning') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
        <AlertTriangle className="w-3 h-3" />
        {message || 'Warning'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
      <XCircle className="w-3 h-3" />
      {message || 'Error'}
    </span>
  );
};

const AdminUploadSettings = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/upload-settings/overview');
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load upload settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const runWriteTest = async () => {
    setTesting(true);
    try {
      const res = await api.post('/admin/upload-settings/test-write');
      setTestResults(res.data);
      if (res.data.all_passed) {
        toast.success('Write test passed!');
      } else {
        toast.error('Write test failed');
      }
    } catch (error) {
      toast.error('Failed to run write test');
      console.error(error);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 mb-1">Upload Settings</h1>
            <p className="text-gray-600">DigitalOcean Spaces CDN storage configuration and status</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={runWriteTest}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {testing ? 'Testing...' : 'Run Write Test'}
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Health Status Banner */}
        <div className={`mb-6 p-4 rounded-xl border ${
          data?.summary?.health_status === 'healthy'
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {data?.summary?.health_status === 'healthy' ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-red-600" />
            )}
            <div>
              <h3 className={`font-semibold ${
                data?.summary?.health_status === 'healthy' ? 'text-green-800' : 'text-red-800'
              }`}>
                {data?.summary?.health_status === 'healthy'
                  ? 'Storage System Healthy — DO Spaces Connected'
                  : 'DO Spaces Not Configured'}
              </h3>
              <p className={`text-sm ${
                data?.summary?.health_status === 'healthy' ? 'text-green-600' : 'text-red-600'
              }`}>
                {data?.spaces_enabled
                  ? 'All file uploads are stored on DigitalOcean Spaces CDN'
                  : 'Set DO_SPACES_KEY and DO_SPACES_SECRET in .env to enable uploads'}
              </p>
            </div>
          </div>
        </div>

        {/* Write Test Results */}
        {testResults && (
          <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <Play className="w-5 h-5" />
                Write Test Results
                {testResults.all_passed ? (
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Passed</span>
                ) : (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">Failed</span>
                )}
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-2">
                {testResults.test_results.map((result, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-700">{result.message}</span>
                    <StatusBadge status={result.status} message={result.status === 'success' ? 'Passed' : 'Failed'} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Tested at: {testResults.timestamp}</p>
            </div>
          </div>
        )}

        {/* Spaces Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <Cloud className="w-5 h-5" />
                Storage Configuration
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Storage Backend</label>
                <p className="mt-1 text-gray-800 font-medium">{data?.storage_backend || 'digitalocean_spaces'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Spaces Enabled</label>
                  <div className="mt-1">
                    {data?.spaces_enabled ? (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" /> Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <XCircle className="w-4 h-4" /> No
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Health</label>
                  <div className="mt-1">
                    <StatusBadge
                      status={data?.summary?.health_status}
                      message={data?.summary?.health_status === 'healthy' ? 'Connected' : 'Not Configured'}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 flex items-center gap-2">
                <Database className="w-5 h-5" />
                Bucket Details
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Bucket</label>
                  <p className="mt-1 text-gray-800 font-medium font-mono">{data?.bucket || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Region
                  </label>
                  <p className="mt-1 text-gray-800 font-medium">{data?.region || '—'}</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
                  <Globe className="w-3 h-3" /> CDN Endpoint
                </label>
                <div className="mt-1 p-3 bg-gray-100 rounded-lg">
                  <code className="text-sm text-gray-800 break-all">{data?.cdn_endpoint || '—'}</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminUploadSettings;
